import type { ReactNode } from "react";
import { Link } from "react-router";
import { AlertTriangle, Check, ExternalLink, XCircle } from "lucide-react";
import type {
  CatalogueDelivery,
  CatalogueItemPricePreview,
} from "@shared-core/data/catalogue-types";
import type { QuoteLine } from "@shared-core/data/pricing-quote";
import { NOT_YET_PRICED } from "@shared-core/domain/checkout-delivery";
import {
  formatCurrencyGbp,
  formatDateTimeOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import type { DeliveryMode } from "../delivery/DeliverySection";

/**
 * The operator price breakdown for the staff ordering page — the "full
 * breakdown" from docs/features/product-catalogue/03_pricing_model.md §8
 * ("Stack order & the operator breakdown"). Pure and props-driven: every
 * figure comes off `preview` (or the page's own CSR-adjusted total), nothing
 * is fetched or recomputed here.
 *
 * Reading order is the whole design: everything that EXPLAINS the number
 * sits above it, and the total is the last line —
 *
 *   1. where the numbers came from (price group · supplier cost)
 *   2. the manufacturing cost, its split, Tharstern's own figure, and how
 *      fresh that cost is (`costLineage`)
 *   3. how the price is built from it — margin, options, speed, discount,
 *      the inactive factors shown honestly as inactive
 *   4. delivery, as a line in the same table (priced, or why not)
 *   5. the guardrail verdict — the last gate before the number
 *   6. the total. Nothing renders below it.
 *
 * Cost-sensitive fields — `contributionMarginPct`, `manufacturingCostPence`,
 * the cost/base-margin line split, the delivery's cost→markup split — arrive
 * `null` when the route's full-cost-breakdown visibility flag strips them
 * for this caller. The panel renders whatever arrives and holds no
 * client-side knowledge of that policy (see `CatalogueItemPricePreview`'s
 * own doc comment).
 */
export function PriceBreakdownPanel({
  preview,
  quotedTotalPence,
  perUnitLabel,
  priceAdjustmentPence,
  deliveryMode,
  quantity,
  canOpenProvenanceLinks,
  costLineage,
  children,
}: {
  preview: CatalogueItemPricePreview;
  /** The final quoted total in pence — the resolved quote plus any CSR
   *  adjustment. What the customer is actually quoted; drives the headline
   *  figure, the GP% chip, and the total-inc-delivery arithmetic. */
  quotedTotalPence: number;
  /** Pre-formatted per-unit text for the headline row, from the page's own
   *  `perUnitLabel()` helper — reused rather than reimplemented so this
   *  figure can never drift from the one shown on the quantity ladder. */
  perUnitLabel: string;
  /**
   * The CSR's own price adjustment, in pence, already included in
   * `quotedTotalPence`. Load-bearing for honesty, not display: the preview
   * request does not carry it, so `contributionMarginPct` and
   * `verdictStatus` were both resolved against the PRE-adjustment price.
   * When this is non-zero the panel must not present either as describing
   * the headline figure — see `MarginChip` and `GuardrailVerdictLine`.
   */
  priceAdjustmentPence: number;
  deliveryMode: DeliveryMode | null;
  /** The quantity `preview` was priced for — combined with `preview.variantId` to address the exact in-house
   *  supplier cost (variant, quantity) the provenance link opens. */
  quantity: number;
  /**
   * Whether the signed-in user may open the dictionary/pricing admin pages
   * the provenance links point at. The panel never decides this itself — a
   * link that lands a CSR or sales rep on a page they are forbidden from is
   * worse than no link, so the page tells the panel via
   * `useCanCurateCatalogue()` rather than the panel guessing from role.
   */
  canOpenProvenanceLinks: boolean;
  /**
   * The cost-freshness trace (`CostFreshnessIndicator`), rendered directly
   * beneath the manufacturing-cost rows because that is the figure it
   * qualifies — "cost fresh · E-123 · 1.2kg", or the stale/failed banner
   * with its Refresh action. Supplied by the page: it fetches, this panel
   * does not.
   */
  costLineage?: ReactNode;
  /**
   * Extra CSR-side adjustment lines (the market-check recommendation, the
   * manual price adjustment) rendered after the backend's factor lines. The
   * panel has no notion of what these mean — they are not part of the
   * operator breakdown the backend sends — so the page supplies them fully
   * rendered.
   */
  children?: ReactNode;
}) {
  const delivery = preview.delivery;
  const deliveryPriced =
    delivery.status === "priced" && delivery.deliveryPricePence != null;
  const exVatLabel =
    delivery.status === "included"
      ? "ex VAT, delivery included"
      : deliveryPriced || delivery.status === "not_yet_priced"
        ? "ex VAT, ex delivery"
        : "ex VAT";

  // The cost rows (cost + its "of which" split) come first in the backend's
  // stack; the lineage slot and Tharstern's reference figure belong with them,
  // before the first line that builds price on top of the cost.
  const firstNonCostIndex = preview.lines.findIndex((line) => !isCostRow(line));
  const costRows =
    firstNonCostIndex === -1
      ? preview.lines
      : preview.lines.slice(0, firstNonCostIndex);
  const factorRows =
    firstNonCostIndex === -1 ? [] : preview.lines.slice(firstNonCostIndex);

  return (
    <div data-testid="price-breakdown-panel" className="space-y-3">
      <ProvenanceLine
        preview={preview}
        quantity={quantity}
        canOpenProvenanceLinks={canOpenProvenanceLinks}
      />
      <CostDrilldownLine
        preview={preview}
        quantity={quantity}
        canOpenProvenanceLinks={canOpenProvenanceLinks}
      />

      <div className="space-y-1.5 text-sm">
        {costRows.map((line, i) => (
          <FactorLine key={`cost-${i}`} line={line} />
        ))}
        {preview.tharsternSellPricePence != null && (
          <SubRow
            label="Tharstern's own sell price (reference)"
            amount={preview.tharsternSellPricePence / 100}
          />
        )}
        {costLineage && <div className="pl-3">{costLineage}</div>}

        {factorRows.map((line, i) => (
          <FactorLine key={`factor-${i}`} line={line} />
        ))}
        {children}

        <DeliveryRow delivery={delivery} deliveryMode={deliveryMode} />
      </div>

      <GuardrailVerdictLine
        status={preview.verdictStatus}
        reason={preview.verdictReason}
        isPreAdjustment={priceAdjustmentPence !== 0}
      />
      <PriceProvenanceLine provenance={preview.priceProvenance} />

      {/* The bottom line. Print-only by design — the ladder and every factor
          above are print figures, and delivery is priced beside the print,
          never folded into it — so when delivery IS priced the all-in
          figure follows as its own final line rather than silently changing
          what the big number means. */}
      <div className="border-t border-border pt-3">
        <div className="flex items-baseline justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold">
              {formatQuotedPrice(quotedTotalPence)}
            </span>
            <span className="text-xs text-muted-foreground">{exVatLabel}</span>
            <MarginChip
              contributionMarginPct={preview.contributionMarginPct}
              isPreAdjustment={priceAdjustmentPence !== 0}
            />
          </div>
          <span className="text-xs text-muted-foreground">
            {perUnitLabel} / unit
          </span>
        </div>
        {deliveryPriced && delivery.deliveryPricePence != null && (
          <div className="mt-1.5 flex items-baseline justify-between text-sm font-medium">
            <span>Total with delivery, ex VAT</span>
            <span className="tabular-nums">
              {formatQuotedPrice(
                quotedTotalPence + delivery.deliveryPricePence,
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function isCostRow(line: QuoteLine): boolean {
  return (
    line.source === "cost" ||
    line.source === "cost_purchases" ||
    line.source === "cost_wages"
  );
}

/**
 * Where the numbers came from — read first, because everything beneath it is
 * an output of these two inputs: the pricing policy that priced the quote and
 * the exact in-house supplier cost (variant × quantity) it was priced
 * from. Links only for users who can open the pages behind them.
 */
function ProvenanceLine({
  preview,
  quantity,
  canOpenProvenanceLinks,
}: {
  preview: CatalogueItemPricePreview;
  quantity: number;
  canOpenProvenanceLinks: boolean;
}) {
  if (!preview.priceGroupName) return null;
  const supplierCostHref =
    canOpenProvenanceLinks && preview.dictionaryItemId && preview.variantId
      ? paths.inHouseSupplierCost({
          variantId: preview.variantId,
          quantity,
        })
      : null;
  return (
    <div className="border-t border-border pt-3 text-xs text-muted-foreground">
      Priced on{" "}
      {canOpenProvenanceLinks && preview.priceGroupId ? (
        <Link
          to={paths.settingsPricingEdit({ groupId: preview.priceGroupId })}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open pricing policy in a new tab"
          className="inline-flex items-center gap-0.5 font-medium text-foreground hover:underline"
        >
          {preview.priceGroupName}
          <ExternalLink className="size-3" />
        </Link>
      ) : (
        <span className="font-medium">{preview.priceGroupName}</span>
      )}
      {supplierCostHref && (
        <>
          {" "}
          ·{" "}
          <Link
            to={supplierCostHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open supplier cost in a new tab"
            className="inline-flex items-center gap-0.5 font-medium text-foreground hover:underline"
          >
            Supplier cost
            <ExternalLink className="size-3" />
          </Link>
        </>
      )}
    </div>
  );
}

/**
 * "Costed on 5351 (cover) and 5830 (text), cheapest suitable presses ·
 * policy v3" — a compact echo of the resolved supplier cost's own per-cost-
 * centre reasons (its record's "Our cost engine" section), linking through
 * to it. Curator-only, like the supplier cost link above it, for the same
 * reason: the record is closed to CSRs and sales reps.
 */
function CostDrilldownLine({
  preview,
  quantity,
  canOpenProvenanceLinks,
}: {
  preview: CatalogueItemPricePreview;
  quantity: number;
  canOpenProvenanceLinks: boolean;
}) {
  // Curator-only sentence, not just the link beside it — its own comment
  // says so, but only the link was actually gated (coordinator review
  // round 6).
  if (!canOpenProvenanceLinks || !preview.costDrilldownLine) return null;
  const supplierCostHref =
    preview.dictionaryItemId && preview.variantId
      ? paths.inHouseSupplierCost({
          variantId: preview.variantId,
          quantity,
        })
      : null;
  return (
    <div className="text-xs text-muted-foreground">
      {preview.costDrilldownLine}
      {supplierCostHref && (
        <>
          {" "}
          ·{" "}
          <Link
            to={supplierCostHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open supplier cost working in a new tab"
            className="inline-flex items-center gap-0.5 font-medium text-foreground hover:underline"
          >
            Supplier cost
            <ExternalLink className="size-3" />
          </Link>
        </>
      )}
    </div>
  );
}

/**
 * "One's engine · Costed 10 Sep 04:12 · checks passed" or "Tharstern estimate
 * · fetched 11 Sep 14:02" — where THIS price came from, on every displayed
 * price (`02_glossary_and_live_costing.md` "Provenance on every price").
 * No engine-version/standards-as-of pair in v1 — a single "Costed <date>"
 * timestamp (`04_handover_2026-09-12.md` "No versioning in v1"). Distinct
 * from `CostFreshnessIndicator`, which traces the manufacturing-cost
 * cache's own freshness; this line is about which system priced the quote
 * at all.
 */
function PriceProvenanceLine({
  provenance,
}: {
  provenance: CatalogueItemPricePreview["priceProvenance"];
}) {
  if (!provenance) return null;
  if (provenance.source === "cis_engine") {
    return (
      <div className="text-xs text-muted-foreground">
        One&apos;s engine · Costed {formatDateTimeOrDash(provenance.costedAt)} ·{" "}
        {provenance.checksPassed ? "checks passed" : "checks failing"}
      </div>
    );
  }
  if (provenance.source === "supplier_quote") {
    return (
      <div className="text-xs text-muted-foreground">
        {provenance.routeLabel} quote · {provenance.tier} · fetched{" "}
        {formatDateTimeOrDash(provenance.fetchedAt)}
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground">
      Tharstern estimate · fetched {formatDateTimeOrDash(provenance.fetchedAt)}
    </div>
  );
}

/** An indented, unsigned, muted row that qualifies the row above it and is
 *  never a charge in its own right. */
function SubRow({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="flex items-baseline justify-between pl-3 text-xs text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">{formatCurrencyGbp(amount, 2)}</span>
    </div>
  );
}

/**
 * One factor line, in the backend's stack order. Two lines never read as a
 * charge even though they carry an `add`-signed `kind`:
 * - `cost_purchases`/`cost_wages` are "of which" splits of the Manufacturing
 *   cost row above them, not additions to it — rendered indented, unsigned,
 *   plain.
 * - `applied === false` factor stubs (market/capacity, "Not active in MVP")
 *   contribute nothing to the total — de-emphasised, and shown as an em-dash
 *   rather than £0.00, which would read as a real zero charge.
 */
function FactorLine({ line }: { line: QuoteLine }) {
  if (line.source === "cost_purchases" || line.source === "cost_wages") {
    return <SubRow label={line.label} amount={line.amount} />;
  }

  const isInactive = line.applied === false;
  const sign = line.kind === "discount" ? "−" : line.kind === "add" ? "+" : "";

  return (
    <div
      className={`flex items-baseline justify-between ${
        line.kind === "subtotal" ? "font-medium" : ""
      } ${isInactive ? "opacity-60" : ""}`}
    >
      <span className="text-muted-foreground">
        {line.label}
        {line.note && (
          <span className="ml-1.5 text-muted-foreground/70">{line.note}</span>
        )}
      </span>
      <span
        className={`tabular-nums ${
          line.kind === "discount" ? "text-[color:var(--color-success)]" : ""
        }`}
      >
        {isInactive && line.amount === 0
          ? "—"
          : `${sign}${formatCurrencyGbp(Math.abs(line.amount), 2)}`}
      </span>
    </div>
  );
}

/**
 * The GP% chip beside the headline.
 *
 * `contributionMarginPct` is resolved against the price the *preview* asked
 * about, which does not include the CSR's manual adjustment (the preview
 * request carries no adjustment; only the basket line does). So when
 * an adjustment is in play the figure is still true — of the pre-adjustment
 * price — and is labelled as such rather than left to read as the margin on
 * the number beside it. A rep knocking £14 off a £64 quote must not see the
 * undiscounted 30% presented as the margin on £50.
 */
function MarginChip({
  contributionMarginPct,
  isPreAdjustment,
}: {
  contributionMarginPct: number | null;
  isPreAdjustment: boolean;
}) {
  if (contributionMarginPct == null) return null;
  return (
    <span className="text-xs font-medium text-muted-foreground">
      · {formatPct(contributionMarginPct)} GP
      {isPreAdjustment && (
        <span className="font-normal"> before adjustment</span>
      )}
    </span>
  );
}

/**
 * Guardrails = the verdict line (design doc §8): quiet when green, unfolds
 * into an amber/red banner only when a guardrail actually bit. It sits
 * directly above the total because it is the last gate the number passes
 * through. `reason` is null exactly when `status === "ok"`.
 *
 * `isPreAdjustment` is why this is not a straight render of `status`. The
 * verdict was evaluated on the pre-adjustment price, but `send` re-evaluates
 * it on the adjusted one, so with an adjustment pending a green "Within
 * limits" would be an affirmative safety claim this screen cannot make — a
 * discount that breaches the contribution floor would still read as fine
 * right up until the send refused. The affirmation is therefore withheld and
 * replaced with a neutral note. An amber/red verdict is still shown: a
 * guardrail that already bit can only bite harder under a discount, and
 * over-warning on a positive adjustment is the safe direction to err.
 */
function GuardrailVerdictLine({
  status,
  reason,
  isPreAdjustment,
}: {
  status: CatalogueItemPricePreview["verdictStatus"];
  reason: string | null;
  isPreAdjustment: boolean;
}) {
  if (status === "ok") {
    if (isPreAdjustment) {
      return (
        <div className="text-xs text-muted-foreground">
          Within limits before your adjustment — guardrails are re-checked
          against the adjusted price when you send.
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 text-xs text-[color:var(--color-success)]">
        <Check className="size-3.5" />
        Within limits
      </div>
    );
  }

  const blocked = status === "blocked";
  return (
    <div
      role={blocked ? "alert" : "status"}
      className={`flex items-start gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
        blocked
          ? "border-[color:var(--color-error,red)]/40 bg-[color:var(--color-error,red)]/5 text-[color:var(--color-error,red)]"
          : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
      }`}
    >
      {blocked ? (
        <XCircle className="mt-0.5 size-3.5 shrink-0" />
      ) : (
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
      )}
      <span>
        <span className="font-medium">
          {blocked ? "Blocked" : "Needs approval"}
        </span>
        {reason && ` — ${reason}`}
        {isPreAdjustment &&
          " (assessed before your adjustment; re-checked on send)"}
      </span>
    </div>
  );
}

/**
 * Modes under which nothing is shipped, so there is no delivery cost to
 * state and the quote is complete without one. Mirrors
 * `_MODES_THAT_SHIP_NOTHING` in `send_catalogue_estimate.py` — the backend's
 * gate keys on the same set, and the two disagreeing is what makes a button
 * enable for a send that then 400s. (`mailing` is in the backend set but
 * this page does not offer the mode.)
 */
function shipsNothing(mode: DeliveryMode | null): boolean {
  return mode === "collect" || mode === "hold" || mode === "mailing";
}

/**
 * Delivery as a line in the breakdown table, never folded into the print
 * figures above it or the headline below (order to invoice doc 104 §5):
 * - `priced` — the price, with how it was reached beneath it: the level,
 *   courier and cost→markup split when this caller may see costs, or "Agreed
 *   price". The all-in total then follows as the panel's final line.
 * - `included` — inside the supplier's price.
 * - `collection` — collected from us, no charge.
 * - `not_yet_priced` — never £0.00: the server's sentence says why. The
 *   order may still go ahead; staff agree the price later.
 * - `address_required` — nothing priced yet. The row has to SAY delivery is
 *   not in the total, or the total moving once an address is picked reads as
 *   a bug. A mode that ships nothing has no delivery cost and nothing missing.
 *
 * `!= null` throughout, never truthiness — an agreed £0.00 is a real answer.
 */
function DeliveryRow({
  delivery,
  deliveryMode,
}: {
  delivery: CatalogueDelivery;
  deliveryMode: DeliveryMode | null;
}) {
  const row = (value: ReactNode, muted = false) => (
    <div
      className={`flex items-baseline justify-between${muted ? " opacity-60" : ""}`}
    >
      <span className="text-muted-foreground">Delivery</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
  switch (delivery.status) {
    case "included":
      return (
        <>
          {row("Included")}
          <SubRowNote>
            Inside the supplier's price above, so the total is the whole quote.
            The customer's estimate says "Included".
          </SubRowNote>
        </>
      );
    case "priced": {
      const level = delivery.serviceLevel
        ? (delivery.options.find(
            (o) => o.serviceLevel === delivery.serviceLevel,
          )?.serviceLevelLabel ?? null)
        : null;
      const how =
        delivery.source === "agreed"
          ? "Agreed price"
          : [
              level,
              delivery.courierName,
              delivery.carriageCostPence != null
                ? `cost ${formatCurrencyGbp(delivery.carriageCostPence / 100, 2)}${
                    delivery.markupPct != null
                      ? ` + ${formatPct(delivery.markupPct)} markup`
                      : ""
                  }`
                : null,
            ]
              .filter(Boolean)
              .join(" · ");
      return (
        <>
          {row(
            delivery.deliveryPricePence == null
              ? NOT_YET_PRICED
              : `+${formatCurrencyGbp(delivery.deliveryPricePence / 100, 2)}`,
          )}
          {how && <SubRowNote>{how}</SubRowNote>}
        </>
      );
    }
    case "collection":
      return row("Collection");
    case "not_yet_priced":
      return (
        <>
          {row(NOT_YET_PRICED, true)}
          {delivery.notPricedMessage && (
            <SubRowNote>{delivery.notPricedMessage}</SubRowNote>
          )}
        </>
      );
    case "address_required":
      return (
        <>
          {row("—", true)}
          <SubRowNote>
            {shipsNothing(deliveryMode)
              ? "Nothing is shipped on this job — no delivery cost; the total is the whole quote."
              : "Not in the total: choose the delivery address."}
          </SubRowNote>
        </>
      );
  }
}

function SubRowNote({ children }: { children: ReactNode }) {
  return <div className="pl-3 text-xs text-muted-foreground">{children}</div>;
}

/** `4.0` → "4%", `2.1` → "2.1%" — whole-percent figures the wire never pads
 *  with a spurious ".0". Shared by the GP% chip and the delivery markup note. */
function formatPct(value: number): string {
  return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}
